import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { COLOR_SYSTEM } from "components/design/design-system";
import { SubscriptionPayment } from "common/models/types";
import { AdminSubscriptionRow } from "common/types/adminSubscription";
import { fetchAdminSubscriptionPayments } from "api/adminSubscriptionService";
import Text from "components/atoms/Text";
import Button from "components/atoms/Button";
import Tag from "components/atoms/Tag";
import Spinner from "components/atoms/Spinner";
import { Flex } from "components/atoms";
import { formatKstDate, formatKrw } from "utils/subscriptionDisplay";
import { log, LogLevel } from "utils/LogUtil";
import { STATUS_META } from "./SubscriptionTable";

interface SubscriptionDetailProps {
  row: AdminSubscriptionRow;
  isBusy: boolean;
  onCancel: (row: AdminSubscriptionRow) => void;
  onRetry: (row: AdminSubscriptionRow) => void;
}

const Container = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 14px;
`;

const Label = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[600]};
`;

const Value = styled.span`
  color: ${COLOR_SYSTEM.get("Gray")?.[900]};
  text-align: right;
  word-break: break-all;
`;

const Divider = styled.div`
  height: 1px;
  background: ${COLOR_SYSTEM.get("Gray")?.[100]};
`;

const PaymentItem = styled.div`
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Empty = styled.div`
  padding: 24px;
  text-align: center;
  color: ${COLOR_SYSTEM.get("Gray")?.[500]};
  font-size: 14px;
`;

const SubscriptionDetail: React.FC<SubscriptionDetailProps> = ({
  row,
  isBusy,
  onCancel,
  onRetry,
}) => {
  const [payments, setPayments] = useState<SubscriptionPayment[]>([]);
  const [loadingPayments, setLoadingPayments] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoadingPayments(true);
      const res = await fetchAdminSubscriptionPayments(row.id, { limit: 20, offset: 0 });
      if (!active) return;
      if (res.error === 0 && res.data) setPayments(res.data.items);
      else {
        setPayments([]);
        log(LogLevel.ERROR, "결제 이력 조회 실패", res.message);
      }
      setLoadingPayments(false);
    })();
    return () => {
      active = false;
    };
  }, [row.id]);

  // 활성/연체/대기/해지중만 해지 가능. 이미 종료(EXPIRED/CANCELLED)면 비활성.
  const cancellable = ["ACTIVE", "PAST_DUE", "PENDING", "CANCELLING"].includes(row.status);

  return (
    <Container>
      <Text textType="H3">구독 상세</Text>

      <Section>
        <Row>
          <Label>회원</Label>
          <Value>
            {row.user?.name || "-"} ({row.user?.email || "-"})
          </Value>
        </Row>
        <Row>
          <Label>상태</Label>
          <Value>
            <Flex alignItems="center" gap="4px" style={{ justifyContent: "flex-end" }}>
              <Tag
                size="Small"
                variant="Tinted"
                color={(STATUS_META[row.status] ?? STATUS_META.NONE).color}
              >
                {(STATUS_META[row.status] ?? STATUS_META.NONE).label}
              </Tag>
              {row.isComp && (
                <Tag size="Small" variant="Tinted" color="Purple">
                  무료제공
                </Tag>
              )}
            </Flex>
          </Value>
        </Row>
        <Row>
          <Label>요금제</Label>
          <Value>{row.planName || row.planCode || "-"}</Value>
        </Row>
        <Row>
          <Label>금액</Label>
          <Value>{row.isComp ? "무료" : formatKrw(row.amount)}</Value>
        </Row>
        <Row>
          <Label>현재 기간</Label>
          <Value>
            {formatKstDate(row.currentPeriodStart)} ~ {formatKstDate(row.currentPeriodEnd)}
          </Value>
        </Row>
        <Row>
          <Label>다음 결제일</Label>
          <Value>{row.nextBillingAt ? formatKstDate(row.nextBillingAt) : "-"}</Value>
        </Row>
        <Row>
          <Label>카드</Label>
          <Value>{row.cardName || "-"}</Value>
        </Row>
        <Row>
          <Label>가입일</Label>
          <Value>{formatKstDate(row.createdAt)}</Value>
        </Row>
      </Section>

      <Flex gap="8px">
        <Button
          buttonType="button"
          color="Negative"
          size="Medium"
          type="Text"
          variant="Outlined"
          disabled={isBusy || !cancellable}
          onClick={() => onCancel(row)}
        >
          구독 취소
        </Button>
        <Button
          buttonType="button"
          color="Secondary"
          size="Medium"
          type="Text"
          variant="Outlined"
          disabled={isBusy}
          onClick={() => onRetry(row)}
        >
          복구(동기화)
        </Button>
      </Flex>

      <Divider />

      <Section>
        <Text textType="Body2Bold">결제 이력</Text>
        {loadingPayments ? (
          <Flex justifyContent="center" style={{ padding: "24px" }}>
            <Spinner />
          </Flex>
        ) : payments.length === 0 ? (
          <Empty>
            {row.isComp
              ? "무료 제공 구독은 결제 이력이 없습니다."
              : "결제 이력이 없습니다."}
          </Empty>
        ) : (
          payments.map((p) => (
            <PaymentItem key={p.id}>
              <Row>
                <Label>{formatKstDate(p.paidAt || p.createdAt)}</Label>
                <Value>
                  <Tag size="Small" variant="Tinted" color={p.status === "PAID" ? "Green" : "Gray"}>
                    {p.status}
                  </Tag>
                </Value>
              </Row>
              <Row>
                <Label>금액</Label>
                <Value>{formatKrw(p.amount)}</Value>
              </Row>
              {p.cardName && (
                <Row>
                  <Label>카드</Label>
                  <Value>{p.cardName}</Value>
                </Row>
              )}
              {p.receiptUrl && (
                <Row>
                  <Label>영수증</Label>
                  <Value>
                    <a href={p.receiptUrl} target="_blank" rel="noreferrer">
                      보기
                    </a>
                  </Value>
                </Row>
              )}
            </PaymentItem>
          ))
        )}
      </Section>
    </Container>
  );
};

export default SubscriptionDetail;
